import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../config/api';
import '../styles/Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, loading, error: authError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await login(formData.email, formData.password);
      
      const storedToken = localStorage.getItem('token');
      console.log('[Login] Session token stored:', !!storedToken);
      
      if (!storedToken) {
        console.error('[Login] Token not found in localStorage after login');
        throw new Error('No authentication token received from server');
      }
      
      const userData = response?.user || response?.data?.user || response?.data || response;
      const role = userData?.role || userData?.type || userData?.userType;
      const isAdmin = role === 'admin';
      
      console.log('[Login] User role:', role, 'Is admin:', isAdmin);
      
      // If admin, generate and store admin token
      if (isAdmin) {
        try {
          console.log('[Login] Generating admin token for admin user...');
          const tokenResponse = await api.post('/api/auth/generate-token', {
            token: storedToken
          });
          
          const adminToken = tokenResponse.data?.token || tokenResponse.data?.data?.token;
          if (adminToken) {
            localStorage.setItem('admin_token', adminToken);
            console.log('[Login] Admin token generated and stored successfully');
          } else {
            console.warn('[Login] No admin token in response, proceeding without it');
          }
        } catch (tokenError) {
          console.warn('[Login] Failed to generate admin token:', tokenError);
          // Continue login even if admin token generation fails
        }
      }
      
      navigate(isAdmin ? '/admin' : '/');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title font-heading">Welcome Back</h1>
        <p className="auth-subtitle">Return to your sacred healing journey</p>
        
        {(error || authError) && (
          <Alert className="alert-error">
            <AlertDescription>{error || authError}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <Input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
          </div>

          <div className="auth-link-row">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>

        <p className="auth-footer">
          Don't have an account? <Link to="/register">Register here</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
